import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Controller } from 'react-hook-form'
import axios from 'axios'
import { useParams } from 'react-router-dom'

import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Checkbox } from '../../ui/checkbox'
import KlassSelect from '../shared/KlassSelect'
import LoadingSpinner from '../../leevo_ui/LoadingSpinner/LoadingSpinner'

function formatShortDate(dateString) {
  if (!dateString) return ''
  const date = new Date(dateString)
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

function getSkillLevelNames(klass, included) {
  if (!klass.relationships?.supportedSkillLevels?.data?.length || !included) {
    return null
  }

  const skillLevelIds = klass.relationships.supportedSkillLevels.data.map((sl) => sl.id)
  const skillLevels = included
    .filter((item) => item.type === 'skillLevel' && skillLevelIds.includes(item.id))
    .map((sl) => sl.attributes.name)

  return skillLevels.length > 0 ? skillLevels.join(', ') : null
}

function ClassRosterForm({ form, onSubmit, isSubmitting, demo = false }) {
  const { facility_id: facilityId } = useParams()
  const [klasses, setKlasses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const { control, handleSubmit } = form

  useEffect(() => {
    async function fetchKlasses() {
      try {
        const response = await axios.get(`/api/facilities/${facilityId}/klasses?include=supported_skill_levels`)
        const included = response.data.included || []
        const klassData = response.data.data.map((klass) => {
          const attrs = klass.attributes
          const startDateStr = formatShortDate(attrs.firstOccurrenceAt)
          const endDateStr = formatShortDate(attrs.finalOccurrenceAt)
          const skillLevelNames = getSkillLevelNames(klass, included)
          const baseName = `${attrs.dayOfWeek} ${attrs.timeOfDay}`
          return {
            id: parseInt(klass.id, 10),
            displayName: skillLevelNames ? `${baseName} - ${skillLevelNames}` : baseName,
            dateRange: endDateStr ? `${startDateStr} - ${endDateStr}` : startDateStr
          }
        })
        setKlasses(klassData)
      } catch (error) {
        console.error('Failed to load classes:', error)
      } finally {
        setIsLoading(false)
      }
    }

    if (facilityId) {
      fetchKlasses()
    } else {
      setIsLoading(false)
    }
  }, [facilityId])

  if (!facilityId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>This report is only available when viewing a specific facility.</p>
        <p className="text-sm mt-2">Please access this report from a facility&apos;s reports page.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label>
            Class <span className="text-red-500">*</span>
          </Label>
          <Controller
            name="klass_id"
            control={control}
            rules={{ required: 'Please select a class' }}
            render={({ field, fieldState }) => (
              <>
                <KlassSelect klasses={klasses} value={field.value} onChange={field.onChange} />
                {fieldState.error && <p className="text-sm text-red-500 mt-1">{fieldState.error.message}</p>}
              </>
            )}
          />
          <p className="text-sm text-gray-500 mt-1">
            Select the class to generate a roster for. Shows all enrolled students with guardian contact info.
          </p>
        </div>

        <div>
          <Label htmlFor="file_format">File Format</Label>
          <Controller
            name="file_format"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select format" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="xls">Excel (XLS)</SelectItem>
                  <SelectItem value="pdf">PDF</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <div hidden={demo}>
          <Label>Delivery Method</Label>
          <Controller
            name="delivery_method"
            control={control}
            render={({ field }) => (
              <div className="space-y-2 mt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="email"
                    checked={field.value === 'email' || field.value === 'both'}
                    disabled={field.value === 'email'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'download') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('email')
                      } else if (field.value === 'both') {
                        field.onChange('download')
                      }
                    }}
                  />
                  <Label htmlFor="email" className="font-normal cursor-pointer">
                    Email me when ready
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="download"
                    checked={field.value === 'download' || field.value === 'both'}
                    disabled={field.value === 'download'}
                    onCheckedChange={(checked) => {
                      if (checked && field.value === 'email') {
                        field.onChange('both')
                      } else if (checked) {
                        field.onChange('download')
                      } else if (field.value === 'both') {
                        field.onChange('email')
                      }
                    }}
                  />
                  <Label htmlFor="download" className="font-normal cursor-pointer">
                    Available for download
                  </Label>
                </div>
              </div>
            )}
          />
        </div>
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? 'Generating Preview...' : 'Preview Report'}
      </Button>
    </form>
  )
}

ClassRosterForm.propTypes = {
  demo: PropTypes.bool,
  form: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired
}

export default ClassRosterForm
